import React, { useMemo } from 'react';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from './DataGridNumberedPagination';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import type { StrapiWorkOrderRow } from '../api/strapiCrm';
import { canShowQuotationPdfPreview } from '../pages/QuotationsPage/quotationWorkflow';
import { WorkOrderStatusHoverLabel } from '../pages/SamplingWorkOrdersPage/WorkOrderStatusHover';
import {
  workOrderJobIssued,
  workOrderLinkedScheduleDocumentId,
} from '../pages/SamplingWorkOrdersPage/workOrderListActions';
import { workOrderStatusDisplay } from '../pages/SamplingWorkOrdersPage/workOrderShared';

/** Konsultan: pelanggan group pada jadwal WO. Selain itu: customer header quotation. */
export function workOrderGridCustomerName(row: StrapiWorkOrderRow): string {
  const header = row.quotation?.customer?.name?.trim() || '';
  if (!row.quotation?.is_consultant_quote) return header || '—';

  const scheduleId = workOrderLinkedScheduleDocumentId(row);
  const schedules = row.sampling_schedules?.length
    ? row.sampling_schedules
    : row.sampling_schedule
      ? [row.sampling_schedule]
      : [];
  const linked = scheduleId
    ? schedules.find((schedule) => String(schedule.documentId ?? '').trim() === scheduleId)
    : null;
  const fromSchedule = linked?.quotation_group?.customer?.name?.trim() || '';
  return fromSchedule || header || '—';
}

function formatDate(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('id-ID');
}

function shortId(documentId: string): string {
  if (documentId.length <= 12) return documentId;
  return `${documentId.slice(0, 8)}…`;
}

type WorkOrderDataGridProps = {
  rows: StrapiWorkOrderRow[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  onView?: (row: StrapiWorkOrderRow) => void;
  onEdit?: (row: StrapiWorkOrderRow) => void;
  onDelete?: (row: StrapiWorkOrderRow) => void;
  onPreviewKupPdf?: (row: StrapiWorkOrderRow) => void;
  onPreviewWorkOrderPdf?: (row: StrapiWorkOrderRow) => void;
  onViewSchedule?: (row: StrapiWorkOrderRow) => void;
  pdfRegenerateFeedback?: { severity: 'success' | 'error'; text: string } | null;
  onClearPdfRegenerateFeedback?: () => void;
  /** When `view-only`, only the view action is shown. */
  actionsMode?: 'full' | 'view-only';
};

export function WorkOrderDataGrid({
  rows,
  isLoading,
  isError,
  error,
  onView,
  onEdit,
  onDelete,
  onPreviewKupPdf,
  onPreviewWorkOrderPdf,
  onViewSchedule,
  pdfRegenerateFeedback = null,
  onClearPdfRegenerateFeedback,
  actionsMode = 'full',
}: WorkOrderDataGridProps) {
  const viewOnly = actionsMode === 'view-only';
  const columns: GridColDef<StrapiWorkOrderRow>[] = useMemo(
    () => [
      {
        field: 'documentId',
        headerName: 'ID',
        minWidth: 120,
        flex: 0.5,
        valueGetter: (_, row) => row.documentId,
        valueFormatter: (v) => shortId(String(v)),
      },
      {
        field: 'job_number',
        headerName: 'Job WO',
        minWidth: 105,
        flex: 0.4,
        sortable: false,
        valueGetter: (_, row) => row.job_number?.trim() ?? '',
        valueFormatter: (v) => (String(v).trim() ? String(v) : '—'),
      },
      {
        field: 'quotation_ref',
        headerName: 'Quotation',
        minWidth: 160,
        flex: 0.85,
        sortable: false,
        valueGetter: (_, row) => {
          const q = row.quotation;
          if (!q) return '';
          return q.quotation_number;
        },
      },
      {
        field: 'customer',
        headerName: 'Customer / perusahaan',
        minWidth: 180,
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => workOrderGridCustomerName(row),
      },
      {
        field: 'quotation_id',
        headerName: 'Quot. ID',
        minWidth: 110,
        flex: 0.4,
        sortable: false,
        valueGetter: (_, row) => row.quotation?.documentId ?? '',
        valueFormatter: (v) => (v ? shortId(String(v)) : ''),
      },
      {
        field: 'work_order_date',
        headerName: 'Tanggal',
        minWidth: 120,
        flex: 0.45,
        valueFormatter: (v) => formatDate(String(v)),
      },
      {
        field: 'progress_status',
        headerName: 'Status',
        minWidth: 140,
        flex: 0.5,
        valueGetter: (_, row) => row.progress_status ?? '',
        valueFormatter: (v) => workOrderStatusDisplay(v == null ? null : String(v)),
        renderCell: ({ row }) => <WorkOrderStatusHoverLabel status={row.progress_status} />,
      },
      {
        field: 'sampleCount',
        headerName: 'Jml. sampel',
        minWidth: 110,
        flex: 0.35,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => row.sampleCount ?? 0,
      },
      {
        field: '__actions',
        headerName: 'Aksi',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width:
          64 +
          (onViewSchedule ? 36 : 0) +
          (onPreviewWorkOrderPdf ? 36 : 0) +
          (onPreviewKupPdf ? 36 : 0) +
          (onView ? 36 : 0) +
          (viewOnly ? 0 : onEdit ? 36 : 0) +
          (viewOnly ? 0 : onDelete ? 36 : 0),
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const quotation = row.quotation;
          const showKup =
            Boolean(onPreviewKupPdf) && quotation != null && canShowQuotationPdfPreview(quotation);
          const showWoPdf = Boolean(onPreviewWorkOrderPdf) && workOrderJobIssued(row);
          const scheduleDocumentId = onViewSchedule ? workOrderLinkedScheduleDocumentId(row) : '';
          return (
          <Box
            sx={{ display: 'flex', gap: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {scheduleDocumentId ? (
              <Tooltip title="Penjadwalan sampling">
                <IconButton
                  size="small"
                  aria-label={`Penjadwalan sampling ${row.job_number ?? row.documentId}`}
                  onClick={() => onViewSchedule?.(row)}
                  data-testid={`work-order-action-schedule-${row.documentId}`}
                >
                  <CalendarMonthOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {showWoPdf ? (
              <Tooltip title="PDF Work order">
                <IconButton
                  size="small"
                  aria-label={`PDF work order ${row.job_number ?? row.documentId}`}
                  onClick={() => onPreviewWorkOrderPdf?.(row)}
                  data-testid={`work-order-action-pdf-${row.documentId}`}
                >
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {showKup ? (
              <Tooltip title="PDF KUP (Kaji Ulang Permintaan)">
                <IconButton
                  size="small"
                  aria-label={`PDF KUP ${quotation?.quotation_number ?? row.documentId}`}
                  onClick={() => onPreviewKupPdf?.(row)}
                  data-testid={`work-order-action-kup-pdf-${row.documentId}`}
                >
                  <AssignmentOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {!viewOnly && onEdit ? (
              <Tooltip title="Ubah work order">
                <IconButton
                  size="small"
                  aria-label={`Ubah work order ${row.documentId}`}
                  onClick={() => onEdit(row)}
                  data-testid={`work-order-action-edit-${row.documentId}`}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {onView ? (
              <Tooltip title={viewOnly ? 'Checklist pra-survey' : 'Lihat detail'}>
                <IconButton
                  size="small"
                  aria-label={`Lihat work order ${row.documentId}`}
                  onClick={() => onView(row)}
                  data-testid={`work-order-action-view-${row.documentId}`}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {!viewOnly && onDelete ? (
              <Tooltip title="Hapus work order">
                <IconButton
                  size="small"
                  aria-label={`Hapus work order ${row.documentId}`}
                  onClick={() => onDelete(row)}
                  data-testid={`work-order-action-delete-${row.documentId}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          );
        },
      },
    ],
    [onDelete, onEdit, onPreviewKupPdf, onPreviewWorkOrderPdf, onView, onViewSchedule, viewOnly],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="error">{error instanceof Error ? error.message : 'Gagal memuat work order.'}</Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="work-orders-data-grid">
      <Snackbar
        open={Boolean(pdfRegenerateFeedback)}
        autoHideDuration={6000}
        onClose={() => onClearPdfRegenerateFeedback?.()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {pdfRegenerateFeedback ? (
          <Alert
            severity={pdfRegenerateFeedback.severity}
            onClose={() => onClearPdfRegenerateFeedback?.()}
            sx={{ width: '100%' }}
          >
            {pdfRegenerateFeedback.text}
          </Alert>
        ) : undefined}
      </Snackbar>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.documentId}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
        autoHeight
        density="compact"
        sx={{ '& .MuiDataGrid-columnHeaders': { fontWeight: 600 } }}
      />
    </Box>
  );
}
